import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from '../api/client'
import type { SearchResult } from '../api/types'
import { t } from '../i18n/lingua'

/**
 * La lente della barra superiore: cerca ovunque e porta dove trovare.
 *
 * Un campo che si apre dall'icona (o con ⌘/Ctrl+K), cerca con un ritardo — non
 * a ogni tasto, che sarebbe una richiesta per lettera — e mostra i risultati
 * **raggruppati per tipo** (CI, rete, software, componenti), ognuno col posto
 * dove aprirlo. Cliccare un risultato ci porta e chiude; Esc chiude.
 *
 * Non è la ricerca di una pagina: è il modo per **saltare** a una cosa quando
 * non si sa in quale pagina vive. Per l'elenco completo di un tipo ci sono le
 * pagine dedicate, e il badge «altri» lo ricorda.
 */
/** A page of the menu the search can jump to. */
export interface SearchPage {
  to: string
  label: string
  section: string
}

function normalize(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

/** The pages matching what is typed, best first: the name starting with it,
 *  then a word of the name, then anywhere in the name, then the section. */
export function matchPages(pages: SearchPage[], query: string, limit = 6): SearchPage[] {
  const q = normalize(query.trim())
  if (!q) return []
  const scored: Array<[number, SearchPage]> = []
  for (const p of pages) {
    const label = normalize(p.label)
    const score = label.startsWith(q) ? 0
      : label.split(/[\s/·-]+/).some((w) => w.startsWith(q)) ? 1
        : label.includes(q) ? 2
          : normalize(p.section).includes(q) ? 3 : -1
    if (score >= 0) scored.push([score, p])
  }
  scored.sort((a, b) => a[0] - b[0] || a[1].label.length - b[1].label.length)
  const seen = new Set<string>()
  return scored.map(([, p]) => p).filter((p) => !seen.has(p.to) && (seen.add(p.to), true)).slice(0, limit)
}

export default function GlobalSearch({ pages = [], favorites = [], recent = [] }: {
  pages?: SearchPage[]
  favorites?: SearchPage[]
  recent?: SearchPage[]
}) {
  const [aperto, setAperto] = useState(false)
  const [q, setQ] = useState('')
  const [dati, setDati] = useState<SearchResult | null>(null)
  const [busy, setBusy] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  // ⌘K / Ctrl+K apre la lente da qualunque punto; «/» pure, se non si sta già
  // scrivendo in un campo.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const k = e.key.toLowerCase()
      if ((e.metaKey || e.ctrlKey) && k === 'k') {
        e.preventDefault()
        setAperto(true)
      } else if (k === 'escape') {
        setAperto(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (aperto) inputRef.current?.focus()
  }, [aperto])

  // Ricerca ritardata: si aspetta che chi scrive si fermi, e si scarta la
  // risposta se nel frattempo la domanda è cambiata (niente risultati vecchi
  // che compaiono in ritardo su una ricerca nuova).
  useEffect(() => {
    if (!aperto) return
    const testo = q.trim()
    if (testo.length < 2) {
      setDati(null)
      setBusy(false)
      return
    }
    let vivo = true
    setBusy(true)
    const timer = setTimeout(() => {
      api.ricercaGlobale(testo)
        .then((r) => { if (vivo) setDati(r) })
        .catch(() => { if (vivo) setDati({ query: testo, groups: [] }) })
        .finally(() => { if (vivo) setBusy(false) })
    }, 250)
    return () => { vivo = false; clearTimeout(timer) }
  }, [q, aperto])

  function chiudi() {
    setAperto(false)
    setQ('')
    setDati(null)
  }

  function vai(to: string) {
    chiudi()
    navigate(to)
  }

  if (!aperto) {
    return (
      <button
        type="button"
        className="topbar__item topbar__lente"
        title={t('Cerca ovunque (⌘K)')}
        aria-label={t('Cerca ovunque')}
        onClick={() => setAperto(true)}
      >
        <Lente />
      </button>
    )
  }

  const gruppi = dati?.groups ?? []
  const pageHits = matchPages(pages, q)
  const vuoto = !busy && q.trim().length >= 2 && gruppi.length === 0 && pageHits.length === 0
  const recentOthers = recent.filter((r) => !favorites.some((f) => f.to === r.to)).slice(0, 6)

  // Arrows move through the results, Enter opens: from the field the first
  // result is one ↓ (or Enter) away.
  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const hits = [...(e.currentTarget.querySelectorAll<HTMLButtonElement>('button.gs__hit'))]
    if (!hits.length) return
    const i = hits.indexOf(document.activeElement as HTMLButtonElement)
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      hits[Math.min(hits.length - 1, i + 1)]?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (i <= 0) inputRef.current?.focus()
      else hits[i - 1]?.focus()
    } else if (e.key === 'Enter' && document.activeElement === inputRef.current) {
      e.preventDefault()
      hits[0]?.click()
    }
  }

  const pageButton = (p: SearchPage) => (
    <button key={p.to} type="button" className="gs__hit gs__hit--pagina" onClick={() => vai(p.to)}>
      <span className="gs__hit-titolo">{p.label}</span>
      <span className="gs__hit-sub">{p.section}</span>
    </button>
  )

  return (
    <>
      <div className="gs-backdrop" onClick={chiudi} />
      <div className="gs" role="dialog" aria-label={t('Ricerca globale')} onKeyDown={onKeyDown}>
        <div className="gs__barra">
          <Lente />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('Vai a una pagina, o cerca nomi, IP, MAC, porte, software, seriali…')}
            aria-label={t('Cerca ovunque')}
          />
          {busy && <span className="gs__spin" aria-hidden="true" />}
          <button type="button" className="gs__chiudi" onClick={chiudi} aria-label={t('Chiudi')}>
            Esc
          </button>
        </div>

        {(gruppi.length > 0 || pageHits.length > 0 || vuoto || q.trim().length < 2) && (
          <div className="gs__risultati">
            {!q.trim() && favorites.length > 0 && (
              <div className="gs__gruppo">
                <div className="gs__gruppo-titolo">{t('Preferiti')}</div>
                {favorites.map(pageButton)}
              </div>
            )}
            {!q.trim() && recentOthers.length > 0 && (
              <div className="gs__gruppo">
                <div className="gs__gruppo-titolo">{t('Pagine recenti')}</div>
                {recentOthers.map(pageButton)}
              </div>
            )}
            {pageHits.length > 0 && (
              <div className="gs__gruppo">
                <div className="gs__gruppo-titolo">{t('Pagine')}</div>
                {pageHits.map(pageButton)}
              </div>
            )}
            {q.trim().length < 2 && (
              <p className="gs__hint">{t('Scrivi il nome di una pagina per andarci, o almeno due caratteri per cercare nell’inventario. ↑↓ per scegliere, Invio per aprire.')}</p>
            )}
            {vuoto && <p className="gs__hint">{t('Nessun risultato per «{q}».', { q: q.trim() })}</p>}
            {gruppi.map((g) => (
              <div key={g.kind} className="gs__gruppo">
                <div className="gs__gruppo-titolo">
                  {etichettaGruppo(g.kind)}
                  {g.more && <span className="gs__altri">{t('altri…')}</span>}
                </div>
                {g.hits.map((h) => (
                  <button key={`${g.kind}-${h.id}-${h.title}`} type="button"
                          className="gs__hit" onClick={() => vai(h.to)}>
                    <span className="gs__hit-titolo">{h.title}</span>
                    {h.subtitle && <span className="gs__hit-sub">{h.subtitle}</span>}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

/** L'etichetta del gruppo per `kind`, tradotta con letterali (che la prova
 *  delle traduzioni riconosce) invece della stringa che arriva dal server. */
function etichettaGruppo(kind: string): string {
  switch (kind) {
    case 'ci': return t('Configuration Item')
    case 'tag': return t('Tag')
    case 'interface': return t('Rete (IP, MAC, porte)')
    case 'port': return t('Porte degli switch (dove si vede il MAC)')
    case 'wireless': return t('Wireless (client, SSID)')
    case 'user': return t('Utenti')
    case 'software': return t('Software')
    case 'component': return t('Componenti (schede, moduli, seriali)')
    case 'asset': return t('Asset')
    case 'service': return t('Business service')
    case 'contract': return t('Contratti')
    default: return kind
  }
}

function Lente() {
  return (
    <svg className="gs__icona" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M15.5 15.5 L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}
